import React, { useRef, useEffect, useReducer, useCallback } from 'react';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import PropTypes from 'prop-types';
import { ProductFamiliesPropType, PromosPropType } from '@propTypes';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import cn from 'classnames';
import { getTranslations } from '@translations';
import { useGTM } from '@hooks';
import MenuItem from './components/MenuItem';
import MenuLink from './components/MenuLink';
import MenuGoBack from './components/MenuGoBack';
import MenuPromosList from './components/MenuPromosList';
import MenuSeeAll from './components/MenuSeeAll';
import styles from './styles/index.module.scss';

const forwardTransitions = {
  enterActive: styles.itemsEnterActive,
  enterDone: styles.itemsEnter,
  exitActive: styles.itemsExitActive,
  exitDone: styles.itemsExit,
};

const backwardTransitions = {
  enterActive: styles.goBackEnterActive,
  enterDone: styles.goBackEnter,
  exitActive: styles.goBackExitActive,
  exitDone: styles.goBackExit,
};

const GO_TO_PROMOS = 'GO_TO_PROMOS';
const GO_BACK_FROM_PROMOS = 'GO_BACK_FROM_PROMOS';

const actions = {
  resetState: 'resetState',
  goForward: 'goForward',
  goBackward: 'goBackward',
  goToNextLevel: 'goToNextLevel',
  goToPreviousLevel: 'goToPreviousLevel',
};

const reducer = (state, action) => {
  switch (action.type) {
    case actions.resetState:
      return {
        items: action.tree,
        navigation: [],
        isGoingBack: null,
        isStartingAnimation: false,
      };
    case actions.goForward:
      return {
        ...state,
        nextId: action.nextId,
        isGoingBack: false,
        isPromosVisible: false,
        isStartingAnimation: true,
      };
    case actions.goToNextLevel:
      return {
        ...state,
        items: action.currentItem.children,
        navigation: [
          ...state.navigation,
          {
            id: action.currentItem.id,
            name: action.currentItem.name,
            url: action.currentItem.url,
          },
        ],
        nextId: null,
        isStartingAnimation: false,
      };
    case actions.goBackward:
      return {
        ...state,
        isGoingBack: true,
        isStartingAnimation: true,
      };
    case actions.goToPreviousLevel:
      return {
        ...state,
        items: action.navigationItems,
        navigation: state.navigation.slice(0, state.navigation.length - 1),
        isStartingAnimation: false,
      };
    case GO_TO_PROMOS:
      return {
        ...state,
        items: [],
        navigation: ['promos'],
        isPromosVisible: true,
        isGoingBack: false,
      };
    case GO_BACK_FROM_PROMOS:
      return {
        ...state,
        items: action.tree,
        navigation: [],
        isPromosVisible: false,
        isGoingBack: true,
      };
    default:
      return state;
  }
};

const MenuItems = ({ isOpen, tree, isPromosEnabled, promos }) => {
  const refContainer = useRef(null);
  const initialState = {
    items: tree,
    nextId: null,
    navigation: [],
    isGoingBack: null,
    isStartingAnimation: false,
    isPromosVisible: false,
  };
  const [pushToGTM] = useGTM();
  const containerClassName = cn(styles.container, {
    [styles.menuOpened]: isOpen,
  });
  const [state, dispatch] = useReducer(reducer, initialState);
  const {
    navigation,
    isGoingBack,
    items,
    nextId,
    isStartingAnimation,
    isPromosVisible,
  } = state;
  const transitionClassName = cn({
    [styles.goBack]: isGoingBack,
    [styles.items]: !isGoingBack,
  });
  const translations = getTranslations();

  useEffect(() => {
    if (!refContainer.current) return;
    if (isOpen) {
      disableBodyScroll(refContainer.current);
      document.body.style.position = 'fixed';
    } else {
      enableBodyScroll(refContainer.current);
      document.body.style.position = 'initial';
      dispatch({ type: actions.resetState, tree });
    }
  }, [isOpen, refContainer.current]);

  const getNavigationItems = useCallback((id, itemTree) => {
    if (itemTree && itemTree.length) {
      for (let i = 0; i < itemTree.length; i++) {
        const child = itemTree[i];
        if (child && child.id === id) {
          return itemTree;
        }
        const result = getNavigationItems(id, child.children);

        if (result) return result;
      }
    }
  }, []);

  const goForward = (event, id) => {
    dispatch({ type: actions.goForward, nextId: id });
  };

  const goBackward = () => {
    dispatch({ type: actions.goBackward });
  };

  const goToNextLevel = useCallback(() => {
    const currentItem = items.find(item => item.id === nextId);
    if (currentItem) dispatch({ type: actions.goToNextLevel, currentItem });
  }, [items, nextId]);

  const goToPreviousLevel = useCallback(() => {
    if (items.length > 0 && navigation.length > 0) {
      const navigationItem = navigation[navigation.length - 1];
      const navigationItems = getNavigationItems(navigationItem.id, tree);
      if (navigationItems)
        dispatch({ type: actions.goToPreviousLevel, navigationItems });
    }
  }, [items, navigation]);

  useEffect(() => {
    if (isGoingBack && isStartingAnimation) goToPreviousLevel();
    else if (isGoingBack === false && isStartingAnimation) goToNextLevel();
  }, [isGoingBack, goToNextLevel, goToPreviousLevel, isStartingAnimation]);

  const pushNavItemToGTM = useCallback(
    (event, { level, type = 'default', destination }) => {
      pushToGTM({
        event: 'click_on_navmenu_item',
        event_trigger: 'navmenu-screen',
        navmenu_item_level: level,
        navmenu_item_type: type,
        navmenu_item_destination: destination,
      });
    },
    [pushToGTM],
  );

  const handlePromosMenuItemClick = useCallback(() => {
    dispatch({ type: GO_TO_PROMOS });
  }, []);

  const handlePromosBackClick = useCallback(() => {
    dispatch({ type: GO_BACK_FROM_PROMOS, tree });
  }, [tree]);

  const promosText = translations('header.subheader.promos.text');
  const showPromosMenuItem =
    promos.length &&
    isPromosEnabled &&
    !isPromosVisible &&
    navigation?.length === 0;

  return (
    <div className={containerClassName} ref={refContainer}>
      <TransitionGroup timeout={400}>
        <CSSTransition
          timeout={{ enter: 0, exit: 400 }}
          key={navigation.length}
          classNames={isGoingBack ? backwardTransitions : forwardTransitions}
        >
          <ul className={transitionClassName}>
            {showPromosMenuItem ? (
              <MenuItem
                item={{ name: promosText }}
                key={promosText}
                onClick={handlePromosMenuItemClick}
                className={styles.item}
                icon="promos"
              />
            ) : null}
            {isPromosEnabled && isPromosVisible && (
              <>
                <MenuGoBack
                  label={promosText}
                  onClick={handlePromosBackClick}
                />
                <MenuPromosList promos={promos} />
              </>
            )}
            {navigation?.length > 0 && !isPromosVisible && (
              <>
                <MenuGoBack
                  label={navigation[navigation.length - 1].name}
                  onClick={goBackward}
                />
                {navigation.length > 0 && (
                  <MenuSeeAll
                    label={navigation[navigation.length - 1].name}
                    url={navigation[navigation.length - 1].url}
                    level={navigation.length}
                    onClick={pushNavItemToGTM}
                    className={styles.item}
                  />
                )}
              </>
            )}
            {items &&
              items.map(item =>
                item.children?.length > 0 ? (
                  <MenuItem
                    item={item}
                    key={item.id}
                    onClick={goForward}
                    className={styles.item}
                  />
                ) : (
                  <MenuLink
                    item={item}
                    key={item.id}
                    onClick={pushNavItemToGTM}
                    className={styles.item}
                  />
                ),
              )}
          </ul>
        </CSSTransition>
      </TransitionGroup>
    </div>
  );
};

MenuItems.propTypes = {
  tree: ProductFamiliesPropType.isRequired,
  isOpen: PropTypes.bool,
  isPromosEnabled: PropTypes.bool,
  promos: PromosPropType.isRequired,
};

MenuItems.defaultProps = {
  isOpen: false,
  isPromosEnabled: false,
};

MenuItems.displayName = 'MenuItems';

export default MenuItems;
